// src/sectors/pilates/reports/staff/overview/ui/StaffOverviewKpiRow.jsx
import React, { useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import PieChart from 'examples/Charts/PieChart';
import DefaultLineChart from 'examples/Charts/LineCharts/DefaultLineChart';

import { pct } from '../utils/overviewFormatters';

// ✅ Pie tekrar tekrar render olmasın
const MemoPieChart = React.memo(PieChart);

function CompactCard({ children }) {
  return <MDBox sx={{ height: '100%', '& > *': { height: '100%' } }}>{children}</MDBox>;
}

function fmtDays(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(n < 10 ? 1 : 0)} gün`;
}
function fmtNoteAvg(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(1);
}

export function StaffOverviewKpiRow({ trainerId, loading, agg }) {
  const theme = useMuiTheme();

  const loadingText = '…';
  const disabledText = '—';
  const hasTrainer = trainerId !== null && trainerId !== undefined;

  const vNum = (val) => (!hasTrainer ? disabledText : loading ? loadingText : String(val ?? 0));
  const vDays = (val) => (!hasTrainer ? disabledText : loading ? loadingText : fmtDays(val));
  const vNote = (val) => (!hasTrainer ? disabledText : loading ? loadingText : fmtNoteAvg(val));

  // ✅ 4 KPI kart için kolon

  const pieModel = useMemo(() => {
    const attended = Number(agg?.attendedSessions ?? 0);
    const noShow = Number(agg?.noShowSessions ?? 0);
    const cancelled = Number(agg?.cancelledSessions ?? 0);
    const burned = Number(agg?.burnedSessions ?? 0);

    const total = attended + noShow + cancelled + burned;
    const safePct = (x) => (total > 0 ? pct((x / total) * 100) : '0%');

    const labels = [
      `Katıldı (${safePct(attended)})`,
      `Gelmedi (${safePct(noShow)})`,
      `İptal (${safePct(cancelled)})`,
      `Yakıldı (${safePct(burned)})`,
    ];

    // total 0 ise boş kalmasın
    const data = total > 0 ? [attended, noShow, cancelled, burned] : [1, 0, 0, 0];

    return {
      total,
      chart: {
        labels,
        datasets: {
          label: 'Seans',
          backgroundColors: ['success', 'warning', 'secondary', 'error'],
          data,
        },
        // ✅ animasyon kapalı (performans)
        options: { animation: false, responsiveAnimationDuration: 0 },
      },
    };
  }, [
    agg?.attendedSessions,
    agg?.noShowSessions,
    agg?.cancelledSessions,
    agg?.burnedSessions,
    agg?.attendPct,
  ]);

  const moneyTrendChart = useMemo(() => {
    const labels = agg?.moneyLabels || [];
    const received = agg?.trendReceivedTry || [];

    if (!hasTrainer || loading) {
      return { labels: [], datasets: [] };
    }

    return {
      labels,
      datasets: [
        {
          label: 'Alınan Ödemeler (₺)',
          color: 'success',
          data: received,
        },
      ],
    };
  }, [agg?.moneyLabels, agg?.trendReceivedTry, hasTrainer, loading]);
  // ✅ UI metinleri tek yerden
  const pieDesc = !hasTrainer
    ? 'Eğitmen seçili değil'
    : loading
    ? 'Yükleniyor…'
    : `Toplam ${pieModel.total} katılımcı  • Katılım: ${pct(agg?.attendPct ?? 0)}`;

  const lineDesc = !hasTrainer
    ? 'Eğitmen seçili değil'
    : loading
    ? 'Yükleniyor…'
    : 'Seçili tarih aralığı';

  // ✅ Section separation (soft)
  const sectionBg = alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.06 : 0.04);

  return (
    <MDBox sx={{ width: '100%' }}>
      {/* ================= KPI BLOCK ================= */}
      <MDBox
        sx={{
          borderRadius: 3,
          p: { xs: 1.5, md: 2 },
          background: sectionBg,
        }}
      >
        <Grid container spacing={3} alignItems="stretch">
          {/* 1 */}
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="warning"
                icon="group"
                title="Toplam Üye"
                count={vNum(agg?.members)}
                percentage={{ color: 'info', amount: '', label: 'Toplam Aktif Üye Sayısı' }}
              />
            </CompactCard>
          </Grid>

          {/* 2 */}

          {/* 3 */}
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="info"
                icon="event_note"
                title="Toplam Seans"
                count={vNum(agg?.totalUniqueSessions)}
                percentage={{ color: 'info', amount: '', label: 'Oluşan Seans Sayısı' }}
              />
            </CompactCard>
          </Grid>

          {/* 4 */}
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="info"
                icon="hourglass_bottom"
                title="Paket Bitirme Ort."
                count={vDays(agg?.avgPackageFinishDays)}
                percentage={{ color: 'info', amount: '', label: 'Paket Tamamlama Hızı' }}
              />
            </CompactCard>
          </Grid>

          {/* 5 */}
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="success"
                icon="payments"
                title="Alınan Ödeme"
                count={vNum(agg?.receivedPaymentsTry)} // 🔁 TL formatın varsa ayrı formatter öneririm
                percentage={{ color: 'success', amount: '', label: 'Toplam Alınan Ödemeler' }}
              />
            </CompactCard>
          </Grid>

          {/* 6 */}
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="error"
                icon="account_balance_wallet"
                title="Alınması Gereken"
                count={vNum(agg?.expectedPaymentsTry)} // 🔁 burayı bağla
                percentage={{ color: 'error', amount: '', label: 'Toplam Alınmayan Ödemeler' }}
              />
            </CompactCard>
          </Grid>

          {/* 7 */}
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="success"
                icon="note_alt"
                title="Seansa Not Girme"
                count={
                  !hasTrainer
                    ? disabledText
                    : loading
                    ? loadingText
                    : pct(agg?.notesCoveragePct ?? 0)
                }
                percentage={{ color: 'success', amount: '', label: 'Notlu Seans Oranı' }}
              />
            </CompactCard>
          </Grid>

          {/* 8 */}
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="warning"
                icon="schedule"
                title="Zamanında Tahsilat"
                count={
                  !hasTrainer
                    ? disabledText
                    : loading
                    ? loadingText
                    : pct(agg?.onTimePaymentPct ?? 0)
                }
                percentage={{
                  color: 'warning',
                  amount: '',
                  label: 'Üyelerden Zamanında Tahsilat Alınıyor mu?',
                }}
              />
            </CompactCard>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <CompactCard>
              <ComplexStatisticsCard
                color="secondary"
                icon="person_off"
                title="Zamanında Yoklama"
                count={
                  !hasTrainer
                    ? disabledText
                    : loading
                    ? loadingText
                    : pct(agg?.onTimeAttendancePct ?? 0)
                }
                percentage={{
                  color: 'secondary',
                  amount: '',
                  label: 'Zamanında Yoklama Alınıyor mu?',
                }}
              />
            </CompactCard>
          </Grid>
        </Grid>
      </MDBox>

      {/* ================= GAP + DIVIDER ================= */}
      {/* ================= GAP ================= */}
      <MDBox mt={4} />

      {/* ================= CHART BLOCK ================= */}
      <MDBox>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={6}>
            <MDBox sx={{ height: '100%', minHeight: 340 }}>
              <MemoPieChart
                icon={{ color: 'info', component: 'leaderboard' }}
                title="Seans Dağılımı"
                description={pieDesc}
                chart={pieModel.chart}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={6}>
            <MDBox sx={{ height: '100%', minHeight: 340 }}>
              <DefaultLineChart
                icon={{ color: 'info', component: 'leaderboard' }}
                title="Tahsilat Trendi"
                description={lineDesc}
                chart={moneyTrendChart}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* küçük footer notu (opsiyonel) */}
        {!hasTrainer && (
          <MDBox mt={1.5} px={0.5}>
            <MDTypography variant="button" color="text" sx={{ opacity: 0.75 }}>
              KPI ve grafikler için önce eğitmen seç.
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}
